import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { ApiService } from '../../core/services/api.service';
import { Calculation, DeferredLiability, NewDeferment } from '../model/defer';
import { map, takeUntil } from 'rxjs/operators';
import { KontomatikResponse } from '../../account/models/verification';
import { MONTHS } from '../../shared/models/month';
import { DefermentStatus, DefermentStatusMessage } from '../../shared/models/deferment-status-message.model';
import { HttpParams, HttpResponse } from '@angular/common/http';

@Injectable({
  providedIn: 'root',
})
export class DefermentsService {
  private unsubscribe = new Subject();

  constructor(
    private apiService: ApiService,
  ) {
  }

  public getCalculation(paymentId: string, intallmentsNo: number[], amount: number): Observable<Calculation> {
    return this.apiService.post<Calculation>(`/payments/${paymentId}/deferment/calculate`, {
      installment: intallmentsNo,
      amount: amount,
    }).pipe(map(value => {
      value.installments = value.installments.map(installment => {
        installment.paymentDate = new Date(installment.paymentDate);
        installment.month = MONTHS.find(month => month.monthNo === (1 + installment.paymentDate.getMonth()));
        return installment;
      });
      value.firstPayment = new Date(value.firstPayment);
      value.paymentDate = new Date(value.paymentDate);
      return value;
    }));
  }

  public resendAcceptCode(paymentId: string): Observable<boolean> {
    return this.apiService.post<any>(`/payments/${paymentId}/deferment/resend-code`, {
      paymentId: paymentId,
    });
  }

  public checkDefermentsSum(): Observable<number> {
    return this.apiService.post<any>('/deferments/sum-not-paid', {});
  }

  public fetchFile(agreementNo: string): Observable<HttpResponse<Blob>> {
    let params: HttpParams = new HttpParams();
    params = params.append('agreementNo', agreementNo);
    return this.apiService.fetchFile('/deferments/download', params);
  }

  public fetchFileForPayment(defer: NewDeferment): Observable<HttpResponse<Blob>> {
    return this.apiService.fetchFile(`/payments/${defer.paymentId}/deferment/download`);
  }

  public create(defer: NewDeferment): Observable<boolean> {
    return this.apiService.post<any>(`/payments/${defer.paymentId}/deferment`, {
      details: defer,
    });
  }

  public confirmWithKontomatik(paymentId: string, kontomatik: KontomatikResponse): Observable<boolean> {
    return this.apiService.post<boolean>(`/payments/${paymentId}/deferment/confirm-with-kontomatik`, {
      paymentId: paymentId,
      kontomatik: kontomatik,
    });
  }

  public signApplication(paymentId: string, code: string): Observable<boolean> {
    return this.apiService.post<any>(`/payments/${paymentId}/deferment/sign-application`, {
      paymentId: paymentId,
      code: code,
    });
  }

  public updateState(state: NewDeferment): Observable<boolean> {
    return this.apiService.post<any>(`/payments/${state.paymentId}/deferment/update`, {
      paymentId: state.paymentId,
      status: state.status,
      details: state,
    });
  }

  public getDeferment(paymentId: string): Observable<NewDeferment> {
    return this.apiService.get<any>(`/payments/${paymentId}/deferment`);
  }

  public subscribeWebSocketMessages() {
    return this.apiService
      .subscribe<DefermentStatusMessage>('deferment-status')
      .pipe(
        takeUntil(this.unsubscribe),
        map(value => {
          value.details.date = new Date(value.details.date);
          value.details.calculation.firstPayment = new Date(value.details.calculation.firstPayment);
          value.details.calculation.installments.forEach((installment, index) => {
            installment.paymentDate = new Date(installment.paymentDate);
            installment.month = MONTHS.find(month => month.monthNo === (1 + installment.paymentDate.getMonth()));
            installment.no = index + 1;
          });
          return value;
        }),
      );
  }

  public stopSubscription() {
    this.unsubscribe.next(true);
  }


  changeDefermentStatus(paymentId: string, status: DefermentStatus) {
    return this.apiService.post<any>(`/payments/${paymentId}/deferment/update-status`, {
      status: status,
    });
  }

  cancelDeferment(paymentId: string) {
    return this.apiService.post<boolean>(`/payments/${paymentId}/deferment/cancel`);
  }

  public getDeferredPayments(id: string): Observable<DeferredLiability[]> {
    return this.apiService.get<DeferredLiability[]>(`/deferred-payments/${id}`)
      .pipe(map(value => value.map(dl => {
          dl.date = dl.date ? new Date(dl.date) : null;
          return dl;
        }),
      ));
  }

}
